/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { defineMessages } from 'react-intl';

export const messages = defineMessages({
  titleHeader: {
    id: 'pages.preview.title.header',
    defaultMessage: 'Preview'
  },
  title: {
    id: 'pages.preview.title',
    defaultMessage: 'Preview assistant'
  },
  subheader: {
    id: 'pages.preview.subheader',
    defaultMessage:
      'Test and refine your assistant, see what your customers will experience.'
  },
  fauxSite: {
    id: 'pages.preview.fauxSite',
    defaultMessage: 'Image of a demo site where web chat will be placed.'
  },
  viewIntegrations: {
    id: 'pages.preview.viewIntegrations',
    defaultMessage: 'See your test integrations'
  },
  customizeChat: {
    id: 'pages.preview.customizeChat',
    defaultMessage: 'Customize your chat UI'
  },
  customizeChatDescription: {
    id: 'pages.preview.customizeChatDescription',
    defaultMessage: 'Update the style to match your brand and our website.'
  },
  customizeChatButton: {
    id: 'pages.preview.customizeChatDescription',
    defaultMessage: 'Customize web chat'
  },
  addBackgroundButton: {
    id: 'pages.preview.addBackgroundButton',
    defaultMessage: 'Add website background'
  },
  copyLinkButton: {
    id: 'pages.preview.copyLinkButton',
    defaultMessage: 'Copy link to share'
  },
  copyFeedback: {
    id: 'pages.preview.copyFeedback',
    defaultMessage: 'Copied'
  },
  copyToClipboard: {
    id: 'pages.preview.copyToClipboard',
    defaultMessage: 'Copy to Clipboard'
  },
  changeBackgroundWebsite: {
    id: 'pages.preview.changeBackgroundWebsite',
    defaultMessage: 'Change background website'
  },
  changeBackground: {
    id: 'pages.preview.changeBackground',
    defaultMessage: 'Change background'
  },
  changeBackgroundSubTitle: {
    id: 'pages.preview.changeBackground.subtitle',
    defaultMessage:
      'Enter a website URL to preview how customers will see your assistant.'
  },
  changeBackgroundHelpText: {
    id: 'pages.preview.changeBackground.helptext',
    defaultMessage:
      'The URL must be publicly available. Do not use private or internal websites.'
  },
  changeBackgroundContinue: {
    id: 'pages.preview.changeBackground.subtitle',
    defaultMessage: 'Continue'
  },
  changeBackgroundCancel: {
    id: 'pages.preview.changeBackground.cancel',
    defaultMessage: 'Cancel'
  },
  changeBackgroundInvalid: {
    id: 'pages.preview.changeBackground.invalid',
    defaultMessage: 'Please enter a valid URL.'
  },
  changeWebChatLayout: {
    id: 'pages.preview.changeWebChatLayout',
    defaultMessage: 'Change web chat layout'
  },
  changeWebChatLayoutLabel: {
    id: 'pages.preview.changeWebChatLayoutLabel',
    defaultMessage:
      'This setting is mainly for demonstration purposes and will only affect the web chat in preview pages, not the web chat embedded on your page.'
  },
  changeWebChatLayoutCancel: {
    id: 'pages.preview.changeWebChatLayoutCancel',
    defaultMessage: 'Cancel'
  },
  changeWebChatLayoutSave: {
    id: 'pages.preview.changeWebChatLayoutSave',
    defaultMessage: 'Save'
  },
  protocolInvalid: {
    id: 'pages.preview.changeBackground.protocolInvalid',
    defaultMessage: 'Only http and https URLs are supported.'
  },
  companyWebsite: {
    id: 'pages.preview.companyWebsite',
    defaultMessage: 'Company website to preview your assistant'
  },
  uploadInstruction: {
    id: 'pages.preview.uploadInstruction',
    defaultMessage:
      'Upload an image of your website to preview how customers will see your assistant. The image is stored for 24 hours.'
  },
  uploadInstructionCpd: {
    id: 'pages.preview.uploadInstructionCpd',
    defaultMessage:
      'Upload an image of your website to preview how customers will see your assistant.'
  },
  uploadLimits: {
    id: 'pages.preview.uploadLimits',
    defaultMessage:
      'Maximum file size is 1 MB. Supported file types are JPEG and PNG.'
  },
  uploadSizeError: {
    id: 'pages.preview.uploadSizeError',
    defaultMessage: 'File exceeds size limit. Maximum file size is 1 MB.'
  },
  uploadFormatError: {
    id: 'pages.preview.uploadFormatError',
    defaultMessage:
      'File type is unsupported. Supported file types are JPEG and PNG.'
  },
  chooseMethod: {
    id: 'pages.preview.chooseMethod',
    defaultMessage: 'Choose method:'
  },
  chooseLayout: {
    id: 'pages.preview.chooseLayout',
    defaultMessage: 'Choose layout:'
  },
  uploadImage: {
    id: 'pages.preview.uploadImage',
    defaultMessage: 'Upload an image'
  },
  enterUrl: {
    id: 'pages.preview.enterUrl',
    defaultMessage: 'Enter URL'
  },
  layoutChoiceDefault: {
    id: 'pages.preview.layoutChoiceDefault',
    defaultMessage: 'Default'
  },
  layoutChoiceFullscreen: {
    id: 'pages.preview.layoutChoiceFullscreen',
    defaultMessage: 'Fullscreen'
  },
  layoutChoiceSidebar: {
    id: 'pages.preview.layoutChoiceSidebar',
    defaultMessage: 'Sidebar'
  },
  removeBackground: {
    id: 'pages.preview.removeBackground',
    defaultMessage: 'Remove background'
  },
  removeBackgroundAlternate: {
    id: 'pages.preview.removeBackgroundAlternate',
    defaultMessage: 'Clear background setting'
  },
  removeBackgroundAlternateCpd: {
    id: 'pages.preview.removeBackgroundAlternateCpd',
    defaultMessage: 'Clear image setting'
  },
  removeFile: {
    id: 'pages.preview.removeFile',
    defaultMessage: 'Remove file'
  },
  removeBackgroundWebsite: {
    id: 'pages.preview.removeBackgroundWebsite',
    defaultMessage: 'Remove background website'
  },
  removeBackgroundInstruction: {
    id: 'pages.preview.removeBackgroundInstruction',
    defaultMessage:
      'Remove the current background image and return to the original default image. If you uploaded an image file, it will no longer be available.'
  },
  dragOrClick: {
    id: 'pages.preview.dragOrClick',
    defaultMessage: 'Drag file or click to upload'
  },
  comingSoon: {
    id: 'pages.preview.comingSoon',
    defaultMessage: 'Coming soon'
  },
  share: {
    id: 'pages.preview.share',
    defaultMessage: 'Share this preview with your team'
  },
  shareDescription: {
    id: 'pages.preview.shareDescription',
    defaultMessage: 'This link is public and can be used in any browser'
  },
  goToPublish: {
    id: 'pages.preview.goToPublish',
    defaultMessage: 'When you’re ready to publish, go to the '
  },
  environmentsPage: {
    id: 'pages.preview.environmentsPage',
    defaultMessage: 'environments page.'
  }
});
